import {useState, useEffect, useCallback, useRef} from 'react'
import PaypalSubscription from './PaypalSubscription'
import {useAuth} from '../modules/auth/core/Auth'
import {toAbsoluteUrl} from '../../_metronic/helpers'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())

// localStorage guard so the dismissible (banner-mode) popup only auto-opens
// once per calendar day.
const todayKey = () => {
  const d = new Date()
  return `trial_pay_prompt_${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

// Calendar days from today until the trial-end day (end-of-day stored value).
const daysUntilTrialEnd = (trialEndIso?: string): number => {
  if (!trialEndIso) return 0
  const te = new Date(trialEndIso)
  const endDay = new Date(te.getFullYear(), te.getMonth(), te.getDate())
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((endDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

const PaymentRequiredModal = () => {
  const {currentUser, setCurrentUser, auth} = useAuth()
  const mode = currentUser?.payment_gate?.mode || 'none'
  const blocking = mode === 'blocking'
  // 'update' = existing paying customer whose card needs refreshing (NOT a trial).
  const isUpdate = currentUser?.payment_gate?.reason === 'update'
  // admin_view = an admin is switched into this account → show the banner but
  // never auto-open the blocking popup. Detect it from the backend gate flag OR
  // the presence of an admin session token on this browser (admin_auth_token is
  // kept in localStorage even while impersonating a client), so the payment wall
  // never blocks an admin operating an unpaid account.
  const adminView =
    !!currentUser?.payment_gate?.admin_view ||
    (typeof window !== 'undefined' && !!localStorage.getItem('admin_auth_token'))
  const daysLeft = daysUntilTrialEnd(currentUser?.trial_period_end as string | undefined)

  const [popupOpen, setPopupOpen] = useState(false)
  const [iframeUrl, setIframeUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Invoice details — prefilled from whatever we already have on the account.
  const [invoiceName, setInvoiceName] = useState('')
  const [invoiceEmail, setInvoiceEmail] = useState('')
  const prefilled = useRef(false)

  // Feedback → support ticket
  const [feedback, setFeedback] = useState('')
  const [feedbackSending, setFeedbackSending] = useState(false)
  const [feedbackSent, setFeedbackSent] = useState(false)

  useEffect(() => {
    if (prefilled.current || !currentUser) return
    const cu = currentUser as any
    const name =
      cu.company_name ||
      [cu.first_name, cu.last_name].filter(Boolean).join(' ').trim()
    setInvoiceName(name || '')
    setInvoiceEmail(cu.invoice_email || cu.email || '')
    prefilled.current = true
  }, [currentUser])

  // Decide whether the popup auto-opens. Blocking → always. Banner → once/day,
  // but only in the last 3 days; earlier than that the red banner shows alone.
  useEffect(() => {
    if (adminView) {
      // Admin viewing the account: banner only, never auto-open the popup.
      setPopupOpen(false)
      setIframeUrl('')
    } else if (mode === 'blocking') {
      setPopupOpen(true)
    } else if (mode === 'banner' && daysLeft <= 3) {
      try {
        if (!localStorage.getItem(todayKey())) setPopupOpen(true)
      } catch (_) {}
    } else {
      setPopupOpen(false)
      setIframeUrl('')
    }
  }, [mode, daysLeft, adminView])

  const reloadUser = useCallback(async () => {
    try {
      const {getUserByToken} = await import('../modules/auth/core/_requests')
      if (auth?.api_token) {
        const {data} = await getUserByToken(auth.api_token)
        if (data) setCurrentUser(data)
      }
    } catch (_) {}
  }, [auth, setCurrentUser])

  const handleSuccess = useCallback(() => {
    setPaymentSuccess(true)
    setIframeUrl('')
    // Reload user so payment_gate clears (token now on file).
    setTimeout(reloadUser, 2000)
  }, [reloadUser])

  // Cardcom redirect posts a message to the parent window on completion.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'payment-success') {
        handleSuccess()
      } else if (event.data?.type === 'payment-failed') {
        setError('הפעולה נכשלה. אנא נסה שוב.')
        setIframeUrl('')
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [handleSuccess])

  // Fallback poll while the iframe is open — some flows don't postMessage.
  // Re-fetch the user; the gate self-clears once the token is saved.
  useEffect(() => {
    if (!iframeUrl) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    pollRef.current = setInterval(reloadUser, 5000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [iframeUrl, reloadUser])

  const startAddCard = async () => {
    if (!invoiceName.trim()) {
      setError('יש להזין שם לחשבונית')
      return
    }
    if (!isValidEmail(invoiceEmail)) {
      setError('יש להזין כתובת אימייל תקינה לקבלת החשבוניות')
      return
    }
    try {
      setLoading(true)
      setError('')
      // Token-only (zero charge) — the trial popup never bills. Invoice name +
      // email are persisted server-side before the card iframe opens.
      const params = new URLSearchParams({
        source: 'trial_popup',
        invoice_name: invoiceName.trim(),
        invoice_email: invoiceEmail.trim(),
      })
      const {data} = await axios.get(`${API_URL}/api/generate-token?${params.toString()}`)
      if (data.url) {
        setIframeUrl(data.url)
      } else {
        setError('שגיאה ביצירת עמוד תשלום')
      }
    } catch (e) {
      setError('שגיאה ביצירת עמוד תשלום. נסה שוב.')
    }
    setLoading(false)
  }

  const sendFeedback = async () => {
    if (!feedback.trim()) return
    try {
      setFeedbackSending(true)
      await axios.post(`${API_URL}/api/tickets`, {
        subject: 'משוב מתקופת הניסיון',
        message: feedback.trim(),
        source: 'trial_popup',
      })
      setFeedbackSent(true)
      setFeedback('')
    } catch (e) {
      // Surface lightly; feedback is best-effort.
      setError('שליחת המשוב נכשלה, נסו שוב מאוחר יותר')
    }
    setFeedbackSending(false)
  }

  const dismissPopup = () => {
    try {
      localStorage.setItem(todayKey(), '1')
    } catch (_) {}
    setPopupOpen(false)
    setIframeUrl('')
    setError('')
  }

  if (mode === 'none') return null

  return (
    <>
      {/* Persistent banner — banner mode only, while the popup is closed */}
      {mode === 'banner' && !popupOpen && !paymentSuccess && (
        <div
          dir='rtl'
          className='d-flex align-items-center justify-content-center gap-3 px-4 py-2'
          style={{
            position: 'fixed',
            // Sit just under the app header instead of covering it.
            top: 'var(--bs-app-header-height, 70px)',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            zIndex: 99,
            background: 'linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '13px',
            boxShadow: '0 4px 14px rgba(220,38,38,0.4)',
          }}
        >
          <i className='bi bi-exclamation-triangle-fill' />
          <span>
            {isUpdate
              ? 'אמצעי התשלום אינו פעיל — יש לעדכן כדי שהמערכת תמשיך לפרסם.'
              : `${
                  daysLeft <= 0
                    ? 'תקופת הניסיון מסתיימת היום!'
                    : daysLeft === 1
                    ? 'תקופת הניסיון מסתיימת מחר!'
                    : `תקופת הניסיון מסתיימת בעוד ${daysLeft} ימים`
                } — ללא הוספת אמצעי תשלום המערכת תפסיק לפרסם.`}
          </span>
          <button
            className='btn btn-sm btn-light fw-bold py-1 px-4'
            style={{borderRadius: 999, color: '#dc2626'}}
            onClick={() => setPopupOpen(true)}
          >
            {isUpdate ? 'עדכון אמצעי תשלום' : 'הוספת אמצעי תשלום'}
          </button>
        </div>
      )}

      {/* Popup / blocking modal */}
      {popupOpen && (
        <div
          className='modal fade show d-block'
          style={{
            backgroundColor: 'rgba(15,23,42,0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 9999,
          }}
          tabIndex={-1}
          // When payment isn't due yet (banner mode), clicking the backdrop
          // closes the popup — the persistent banner stays so it's always
          // reachable again. Blocking mode cannot be dismissed.
          onClick={(e) => {
            if (!blocking && !paymentSuccess && e.target === e.currentTarget) dismissPopup()
          }}
        >
          <div
            className='modal-dialog modal-dialog-centered'
            style={{maxWidth: 540}}
          >
            <div
              dir='rtl'
              className='modal-content border-0 overflow-hidden'
              style={{borderRadius: 24, boxShadow: '0 24px 70px rgba(15,23,42,0.35)'}}
            >
              {/* Gradient hero */}
              <div
                className='position-relative text-center px-8 pt-8 pb-6'
                style={{background: 'linear-gradient(135deg,#1877F2 0%,#0b5fce 100%)'}}
              >
                {/* Dismiss only allowed in banner (non-blocking) mode */}
                {!blocking && !paymentSuccess && (
                  <button
                    className='btn btn-sm btn-icon position-absolute'
                    style={{top: 14, insetInlineStart: 14, color: '#fff', opacity: 0.85}}
                    onClick={dismissPopup}
                    title='סגירה'
                  >
                    <i className='bi bi-x-lg fs-4' />
                  </button>
                )}
                <div
                  className='d-inline-flex align-items-center justify-content-center bg-white rounded-pill px-4 py-2 mb-5'
                  style={{boxShadow: '0 6px 18px rgba(0,0,0,0.12)'}}
                >
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/logos/d-code-logo-light.svg')}
                    className='h-25px'
                    style={{
                      // Force the logo to render in Facebook blue (#1877F2)
                      filter:
                        'brightness(0) saturate(100%) invert(36%) sepia(89%) saturate(1583%) hue-rotate(202deg) brightness(97%) contrast(94%)',
                    }}
                  />
                </div>
                <div
                  className='d-flex align-items-center justify-content-center mx-auto mb-4'
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.18)',
                  }}
                >
                  <i
                    className={`bi ${paymentSuccess ? 'bi-check-lg' : 'bi-credit-card-2-front'} text-white`}
                    style={{fontSize: '1.9rem'}}
                  />
                </div>
                <h2 className='fw-bold text-white mb-1'>
                  {paymentSuccess
                    ? 'אמצעי התשלום נשמר בהצלחה!'
                    : isUpdate
                    ? blocking
                      ? 'המנוי מושהה — נדרש עדכון תשלום'
                      : 'יש לעדכן את אמצעי התשלום'
                    : blocking
                    ? 'תקופת הניסיון הסתיימה'
                    : 'תקופת הניסיון מסתיימת בקרוב'}
                </h2>
                <div className='text-white fs-7' style={{opacity: 0.85}}>
                  {paymentSuccess
                    ? 'תודה! מעדכנים את החשבון שלך...'
                    : isUpdate
                    ? 'עדכנו את פרטי התשלום כדי שהמנוי יימשך'
                    : blocking
                    ? 'להמשך השימוש יש להסדיר את התשלום'
                    : 'הוסיפו אמצעי תשלום — ללא חיוב כעת'}
                </div>
              </div>
              <div className='modal-body text-center px-8 pb-8 pt-6'>
                {paymentSuccess ? (
                  <div className='py-10'>
                    <i className='bi bi-check-circle-fill text-success' style={{fontSize: '4rem'}}></i>
                    <p className='fs-4 mt-5 text-gray-700'>
                      אמצעי התשלום נקלט. העמוד יתרענן בעוד רגע...
                    </p>
                  </div>
                ) : iframeUrl ? (
                  <iframe
                    src={iframeUrl}
                    style={{width: '100%', height: '500px', border: 'none'}}
                    title='אמצעי תשלום'
                  />
                ) : (
                  <div className='pt-2'>
                    <p className='fs-5 text-gray-700 mb-0'>
                      {isUpdate ? (
                        <>
                          אמצעי התשלום אינו פעיל.
                          <br />
                          עדכנו את פרטי התשלום כדי שהמנוי יימשך והמערכת תמשיך לפרסם.
                        </>
                      ) : blocking ? (
                        <>
                          תקופת הניסיון הסתיימה.
                          <br />
                          על מנת להמשיך להשתמש במערכת, יש להוסיף אמצעי תשלום.
                        </>
                      ) : (
                        <>
                          תקופת הניסיון מסתיימת בקרוב.
                          <br />
                          הוסיפו אמצעי תשלום עכשיו כדי להבטיח המשך פעילות רציפה.
                        </>
                      )}
                    </p>
                    {(currentUser?.payment_gate?.amount || 0) > 0 && (() => {
                      const total = currentUser!.payment_gate!.amount as number
                      const base = Math.round((total / 1.18) * 100) / 100
                      const vat = Math.round((total - base) * 100) / 100
                      // trial_period_end is end-of-day, so the trial is valid
                      // through that calendar day and the first charge is the
                      // NEXT day. Use calendar-day math (local time).
                      const trialEnd = currentUser?.trial_period_end
                        ? new Date(currentUser.trial_period_end as string)
                        : null
                      const billingStart = trialEnd
                        ? new Date(trialEnd.getFullYear(), trialEnd.getMonth(), trialEnd.getDate() + 1)
                        : null
                      const todayMidnight = new Date()
                      todayMidnight.setHours(0, 0, 0, 0)
                      const daysUntil = billingStart
                        ? Math.max(0, Math.round((billingStart.getTime() - todayMidnight.getTime()) / (1000 * 60 * 60 * 24)))
                        : 0
                      const trialEndStr = billingStart
                        ? billingStart.toLocaleDateString('he-IL', {day: '2-digit', month: '2-digit', year: 'numeric'})
                        : ''
                      return (
                        <div className='rounded-3 p-5 mt-6 text-start w-100' style={{background: '#f8f9ff', border: '1px solid #e7e9ff'}}>
                          <div className='d-flex justify-content-between text-gray-700 fs-6'>
                            <span>מחיר חודשי</span>
                            <span>₪{base.toLocaleString()}</span>
                          </div>
                          <div className='d-flex justify-content-between text-gray-700 fs-6 mt-1'>
                            <span>מע״מ 18%</span>
                            <span>₪{vat.toLocaleString()}</span>
                          </div>
                          <div className='d-flex justify-content-between align-items-center text-gray-700 fs-6 mt-1'>
                            <span>סה״כ לחודש (כולל מע״מ)</span>
                            <span className='fw-bold'>₪{total.toLocaleString()}</span>
                          </div>
                          <div className='separator my-3'></div>
                          {isUpdate ? (
                            /* Existing customer: only the upcoming charge, no ₪0-now trial framing */
                            <div className='d-flex justify-content-between align-items-center'>
                              <span className='fs-7 text-gray-700'>
                                {blocking || daysUntil <= 0
                                  ? 'החיוב הבא'
                                  : `החיוב הבא בעוד ${daysUntil} ${daysUntil === 1 ? 'יום' : 'ימים'}`}
                                {trialEndStr ? ` (${trialEndStr})` : ''}
                              </span>
                              <span className='fs-3 fw-bold text-primary'>₪{total.toLocaleString()}</span>
                            </div>
                          ) : blocking ? (
                            /* Trial ended → the card is charged the real amount now */
                            <div className='d-flex justify-content-between align-items-center'>
                              <span className='fs-6 text-gray-700'>חיוב כעת</span>
                              <span className='fs-3 fw-bold text-primary'>₪{total.toLocaleString()}</span>
                            </div>
                          ) : (
                            <>
                              {/* Trial (not ended yet): nothing now, full amount on trial-end day */}
                              <div className='d-flex justify-content-between align-items-center'>
                                <span className='fs-6 text-gray-700'>חיוב כעת</span>
                                <span className='fs-3 fw-bold text-success'>₪0</span>
                              </div>
                              <div className='d-flex justify-content-between align-items-center mt-1'>
                                <span className='fs-7 text-gray-700'>
                                  {daysUntil <= 0
                                    ? 'החיוב הראשון'
                                    : `החיוב הראשון בעוד ${daysUntil} ${daysUntil === 1 ? 'יום' : 'ימים'}`}
                                  {trialEndStr ? ` (${trialEndStr})` : ''}
                                </span>
                                <span className='fs-4 fw-bold text-primary'>₪{total.toLocaleString()}</span>
                              </div>
                            </>
                          )}
                        </div>
                      )
                    })()}
                    {/* Invoice details — prefilled, editable */}
                    <div className='mx-auto mt-8 text-start' style={{maxWidth: 420}}>
                      <div className='mb-4'>
                        <label className='form-label fs-7 text-muted'>שם לחשבונית</label>
                        <input
                          type='text'
                          className='form-control form-control-solid'
                          placeholder='שם החברה / שם מלא'
                          value={invoiceName}
                          onChange={(e) => setInvoiceName(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className='form-label fs-7 text-muted'>אימייל לקבלת חשבוניות</label>
                        <input
                          type='email'
                          dir='ltr'
                          className='form-control form-control-solid'
                          placeholder='name@example.com'
                          value={invoiceEmail}
                          onChange={(e) => setInvoiceEmail(e.target.value)}
                        />
                      </div>
                    </div>

                    {error && <div className='alert alert-danger mt-5'>{error}</div>}
                    <div className='mt-7 d-flex flex-column align-items-stretch gap-3'>
                      <button
                        className='btn btn-primary btn-lg w-100 fw-bold'
                        style={{borderRadius: 14, paddingTop: 14, paddingBottom: 14}}
                        onClick={startAddCard}
                        disabled={loading}
                      >
                        {loading ? (
                          <span className='spinner-border spinner-border-sm me-2'></span>
                        ) : (
                          <i className='bi bi-credit-card me-2'></i>
                        )}
                        {isUpdate ? 'עדכון אמצעי תשלום' : blocking ? 'לתשלום והמשך' : 'הוספת אמצעי תשלום'}
                      </button>
                      {/* Shown only when PayPal is turned on for this customer. */}
                      <PaypalSubscription compact />
                      {!blocking && (
                        <button className='btn btn-light w-100' style={{borderRadius: 14}} onClick={dismissPopup}>
                          אזכיר לי מאוחר יותר
                        </button>
                      )}
                    </div>

                    {/* Feedback → support ticket (trial experience only) */}
                    {!isUpdate && (
                    <div>
                    <div className='separator my-8'></div>
                    <div className='mx-auto text-start' style={{maxWidth: 420}}>
                      <label className='form-label fw-semibold'>
                        איך הייתה החוויה שלך? נשמח לשמוע 💬
                      </label>
                      {feedbackSent ? (
                        <div className='alert alert-success mb-0'>תודה! המשוב נשלח לצוות שלנו.</div>
                      ) : (
                        <>
                          <textarea
                            className='form-control form-control-solid'
                            rows={3}
                            placeholder='ספרו לנו מה אהבתם, ומה נוכל לשפר...'
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                          />
                          <button
                            className='btn btn-light-primary btn-sm mt-3'
                            onClick={sendFeedback}
                            disabled={feedbackSending || !feedback.trim()}
                          >
                            {feedbackSending ? (
                              <span className='spinner-border spinner-border-sm me-2'></span>
                            ) : (
                              <i className='bi bi-send me-2'></i>
                            )}
                            שליחת משוב
                          </button>
                        </>
                      )}
                    </div>
                    </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export {PaymentRequiredModal}
